import { CommentEventType, useEventSubscription } from '@pwc/event-hub-ui';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {
  USER_ROLES,
  UserRoleType,
  saveGlobalToggleState,
} from '@pwc/pwc-deals-web-common';

import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import CircleIcon from '@mui/icons-material/Circle';
import { setCommentPanelOpened } from 'features/comments/commentsSlice';

import featureFlags from 'common/helpers/featureFlags';
import { SvgIconProps } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import {
  getUserRole,
  setUserRole,
} from 'common/redux/security/loggedInUser/loggedInUserSlice';
import { useHistory, useLocation } from 'react-router-dom';
import {
  GLOBAL_TOGGLE_CLIENT_DIALOG_HIDE_SESSION_STORAGE,
  GlobalToggleClientDialog,
} from './GlobalToggleClientDialog';

const urlAnyMatchesReportsUI = (str: string) => {
  const pattern = /^\/projects\/.+\/reports.*$/;
  const result = pattern.test(str);
  return result;
};

const urlMatchesReportsUI = (str: string) => {
  const pattern = /^\/projects\/.+\/reports\/?$/;
  const result = pattern.test(str);
  return result;
};

const urlMatchesStructuredAnalyses = (str: string, role?: UserRoleType) => {
  /**
   * TODO: jm - Find a better solution inside structured analyses MFE
   * where we return from Client View back to PwC User View
   */
  const pattern = /(quality-of-earnings|net-working-capital|debt-debt-like)$/;
  const result = pattern.test(str) && role === USER_ROLES.PWC_USER;
  return result;
};

const urlMatchesViewTopic = (str: string, role?: UserRoleType) => {
  /**
   * TODO: jm - Find a better solution inside project-topics MFE
   * where the versioning toggle is buggy and needs to be fixed.
   * Only then can we remove this function.
   */
  const pattern = /^\/projects\/.+\/topic\/.+\/view\/.+\/?$/;
  const result = pattern.test(str);
  return result && role === USER_ROLES.PWC_USER;
};

const prepareReportBuilderUrl = (str: string) => {
  const parts = str.split('/');
  const extractedString = `/${parts[1]}/${parts[2]}/${parts[3]}`;
  return extractedString;
};

export const useGlobalToggle = () => {
  const { pathname } = useLocation();
  const history = useHistory();

  const afterUserRoleChanged = useCallback(
    (role?: UserRoleType) => {
      if (
        urlMatchesReportsUI(pathname) ||
        urlMatchesViewTopic(pathname, role) ||
        urlMatchesStructuredAnalyses(pathname, role)
      ) {
        window.location.reload();
      }
      if (urlAnyMatchesReportsUI(pathname)) {
        history.push(prepareReportBuilderUrl(pathname));
      }
    },
    [pathname, history]
  );

  return { afterUserRoleChanged };
};

export const GlobalToggle = () => {
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const { afterUserRoleChanged } = useGlobalToggle();

  const isDialogHidden =
    window.sessionStorage.getItem(
      GLOBAL_TOGGLE_CLIENT_DIALOG_HIDE_SESSION_STORAGE
    ) === 'true';

  const userRole = useSelector(getUserRole) as UserRoleType;

  const [showClientDialog, setShowClientDialog] = useState(false);
  const [ribbonPanelOpen, setRibbonPanelOpen] = useState(false);
  const [viewAsValue, setViewAsValue] = useState(userRole);

  useEventSubscription(
    'commenting-open-comment-panel' as CommentEventType,
    ({ data }) => {
      dispatch(setCommentPanelOpened(data?.open || true));
    }
  );

  useEventSubscription(
    '@ribbon-panel-opened' as CommentEventType,
    ({ data }) => {
      setRibbonPanelOpen(data);
    }
  );

  const disabledGlobalToggle = useMemo(() => {
    if (ribbonPanelOpen) {
      return true;
    }

    const disabledPaths = [
      'create-report',
      'privacy-statement',
      'cookie-statement',
      'terms-of-use',
      'settings',
      'notifications',
    ];
    let isDisabledPath = false;

    disabledPaths.forEach((path) => {
      if (pathname.indexOf(path) > -1) {
        isDisabledPath = true;
      }
    });

    if (isDisabledPath) {
      return true;
    }
    return false;
  }, [pathname, ribbonPanelOpen]);

  useEffect(() => {
    setViewAsValue(userRole);
  }, [userRole]);

  const onConfirmClientDialog = useCallback(
    (role) => {
      saveGlobalToggleState(role);
      dispatch(setUserRole(role));
      afterUserRoleChanged(role);
    },
    [dispatch, afterUserRoleChanged]
  );

  const handleChange:
    | ((event: SelectChangeEvent<UserRoleType>, child: React.ReactNode) => void)
    | undefined = (event) => {
    const value = event.target.value as UserRoleType;

    if (value === USER_ROLES.CLIENT) {
      if (isDialogHidden) {
        onConfirmClientDialog(value);
      } else {
        setShowClientDialog(true);
        setViewAsValue(USER_ROLES.CLIENT);
      }
    } else {
      saveGlobalToggleState(value);
      dispatch(setUserRole(value));
      afterUserRoleChanged(value);
    }
  };

  const roleOptions = useMemo(() => {
    return [
      {
        id: USER_ROLES.PWC_USER,
        label: 'PwC',
        dotColor: '#D93954',
      },
      {
        id: USER_ROLES.CLIENT,
        label: 'Client',
        dotColor: '#FFB600',
      },
      // TODO: - to be enabled in the future
      // {
      //   id: USER_ROLES.THIRD_PARTY,
      //   label: 'Third Party',
      //   dotColor: '#0089EB',
      //   disabled: true,
      // },
    ];
  }, []);

  if (!featureFlags.isGlobalToggleEnabled) {
    return null;
  }

  return (
    <>
      <GlobalToggleClientDialog
        open={showClientDialog}
        onConfirm={() => {
          setShowClientDialog(false);
          onConfirmClientDialog(viewAsValue);
        }}
        onClose={() => {
          setViewAsValue(userRole);
          setShowClientDialog(false);
        }}
      />
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          fontSize: '0.875rem',
        }}
      >
        <Box
          sx={{
            mb: 0,
            color: '#767676',
          }}
          component="label"
          id="global-toggle-select-label"
        >
          View tab as
        </Box>
        <Box>
          <Select
            disabled={disabledGlobalToggle}
            IconComponent={({ className }) => {
              let isOpen = false;
              const iconProps: SvgIconProps = {
                sx: {
                  right: '0.25rem',
                  pointerEvents: 'none',
                  position: 'absolute',
                },
                color: disabledGlobalToggle ? 'disabled' : 'primary',
              };
              if (className.indexOf('iconOpen') > -1) {
                isOpen = true;
              }
              if (isOpen) {
                return <KeyboardArrowUpIcon {...iconProps} />;
              }
              return <KeyboardArrowDownIcon {...iconProps} />;
            }}
            sx={{
              width: '121px',
              height: '34px',
              mr: '1rem',
              fontSize: '0.875rem',
              '& .MuiOutlinedInput-input': {
                display: 'flex',
                alignItems: 'center',
                '& .globalToggleCircle': {
                  display: 'none',
                },
              },
              '& .Mui-disabled': {
                WebkitTextFillColor: '#878787',
              },
              '&.Mui-disabled  .MuiOutlinedInput-notchedOutline': {
                borderColor: '#464646',
              },
              '&.Mui-focused  .MuiOutlinedInput-notchedOutline': {
                border: 'var(--s1px) solid var(--rte-border-color) !important',
              },
              '& .MuiOutlinedInput-notchedOutline': {
                border: 'var(--s1px) solid var(--rte-border-color) !important',
              },
            }}
            size="small"
            labelId="global-toggle-select-label"
            id="global-toggle-select"
            value={viewAsValue}
            onChange={handleChange}
            inputProps={{
              MenuProps: {
                sx: {
                  position: 'absolute',
                  '& .MuiPaper-root': {
                    top: '51px !important',
                    background: 'var(--menuitem-background)',
                    border:
                      'var(--s1px) solid var(--rte-border-color) !important',
                  },
                },
                disableScrollLock: true,
              },
            }}
          >
            {roleOptions.map((item) => (
              <MenuItem
                key={item.id}
                value={item.id}
                sx={{
                  width: '121px',
                  fontSize: '0.875rem',
                  background: 'var(--menuitem-background)',
                  '&.Mui-selected': {
                    background: 'var(--menuitem-background) !important',
                  },
                  '&:hover': {
                    backgroundColor:
                      'var(--menuitem-background-active) !important',
                    cursor: 'pointer',
                  },
                }}
              >
                <CircleIcon
                  className="globalToggleCircle"
                  htmlColor={item.dotColor}
                  fontSize="inherit"
                  sx={{
                    fontSize: '0.75rem',
                    mr: '0.5rem',
                  }}
                />
                {item.label}
              </MenuItem>
            ))}
          </Select>
        </Box>
      </Box>
    </>
  );
};
